// FileViewer.jsx
import React, { useEffect, useRef } from "react";
import WebViewer from "@pdftron/webviewer";
import {useParams} from "react-router-dom";


function parseScaleString(scaleStr) {
  try {
    if (!scaleStr.includes(":")) return null;

    const [a, b] = scaleStr.split(":").map(Number);
    if (!a || !b) return null;

    return [
      [1.0, "mm"],
      [b / 1000, "m"],
    ];
  } catch {
    return null;
  }
}


export const FileViewer = ({
  initialDoc,
   projectName,
  onReady,
  scale = "1:100",
  style = { height: "100%", width: "100%" },
  python = import.meta.env.VITE_PYTHON_API_URL || "http://localhost:8000",
  }) => {
  const viewerRef = useRef(null);
  const instanceRef = useRef(null);
  const clickTimer = useRef(null);

  const [mlLoading, setMlLoading] = React.useState(false);
  const [mlProgress, setMlProgress] = React.useState(0);
  const [canRunML, setCanRunML] = React.useState(false);
  const [mlChecked, setMlChecked] = React.useState(false);


  const apiUrl = import.meta.env.VITE_API_URL;
  let {projectId} = useParams();
  const filePath= new URL(initialDoc).pathname ;

  // Convert normalized polygon -> PDF coordinates (page units)
  const mapNormalizedToPdfPoints = async (pageNumber, pts) => {
    const inst = instanceRef.current;
    if (!inst) return [];
    const doc = inst.Core.documentViewer.getDocument();
    const pageInfo = doc.getPageInfo(pageNumber);
    const pageW = pageInfo.width;
    const pageH = pageInfo.height;

    // NOTE: backend polygons were normalized against the rendered image with y measured from top (0 at top).
    // The frontend earlier expected to convert with y = (1 - ny) * pageH in many places; to stay compatible
    // we will map ny->viewer using the same formula used previously in your code:
    return pts.map(([nx, ny]) => ({
      x: nx * pageW,
      y: (1 - ny) * pageH,
    }));
  };

  // Draw polygon as annotation (works with WebViewer v10+)
  async function drawPolygon(pageNum, polygon) {
    if (!polygon || polygon.length < 3) return;
    const inst = instanceRef.current;
    const Core = inst.Core;
    const annotManager = Core.annotationManager;

    const pdfPts = await mapNormalizedToPdfPoints(pageNum, polygon);
    // create PolygonAnnotation with points property (supported)
    const PolygonAnnotation = Core.Annotations.PolygonAnnotation;
    const annot = new PolygonAnnotation({
      PageNumber: pageNum,
      StrokeColor: new Core.Annotations.Color(255, 0, 0),
      FillColor: new Core.Annotations.Color(255, 0, 0),
      Opacity: 0.18,
      StrokeThickness: 1.5,
      // annot expects points as [{x,y},...]
      // we supply them directly — WebViewer will accept plain objects
      points: pdfPts,
    });

    annotManager.addAnnotation(annot);
    annotManager.redrawAnnotation(annot);
  }

  // Draw bounding box as annotation
  async function drawBoundingBox(pageNum, bbox, className = "detection") {
    if (!bbox) return;
    const inst = instanceRef.current;
    const Core = inst.Core;
    const annotManager = Core.annotationManager;

    // Set colors based on class
    let strokeColor, fillColor;
    switch (className) {
      case 'door':
        strokeColor = new Core.Annotations.Color(0, 0, 255); // Blue
        fillColor = new Core.Annotations.Color(0, 0, 255);
        break;
      case 'window':
        strokeColor = new Core.Annotations.Color(0, 255, 255); // Cyan
        fillColor = new Core.Annotations.Color(0, 255, 255);
        break;
      case 'zone':
        strokeColor = new Core.Annotations.Color(128, 0, 128); // Purple
        fillColor = new Core.Annotations.Color(128, 0, 128);
        break;
      default:
        strokeColor = new Core.Annotations.Color(0, 255, 0); // Green
        fillColor = new Core.Annotations.Color(0, 255, 0);
    }

    // Create rectangle annotation for bounding box
    const RectangleAnnotation = Core.Annotations.RectangleAnnotation;
    const annot = new RectangleAnnotation({
      PageNumber: pageNum,
      X: bbox.x1,
      Y: bbox.y1,
      Width: bbox.width,
      Height: bbox.height,
      StrokeColor: strokeColor,
      FillColor: fillColor,
      Opacity: 0.15,
      StrokeThickness: 2,
    });

    // Add label
    const textAnnot = new Core.Annotations.FreeTextAnnotation({
      PageNumber: pageNum,
      X: bbox.x1,
      Y: bbox.y1 - 20,
      Width: 100,
      Height: 20,
      StrokeColor: strokeColor,
      TextColor: strokeColor,
      Contents: className,
      FontSize: 12,
    });

    annotManager.addAnnotation(annot);
    annotManager.addAnnotation(textAnnot);
    annotManager.redrawAnnotation(annot);
    annotManager.redrawAnnotation(textAnnot);
  }

  // const token = localStorage.getItem("token");

  useEffect(() => {
  if (!projectId) return;

  let cancelled = false;

    (async () => {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/api/takeoff/architectural-objects/exists/${projectId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              // "Authorization": `Bearer ${token}`,
            },
            credentials: "include",
          }
        );

        const data = await res.json();
        if (cancelled) return;

        setCanRunML(!data.exists); // exists → ML skip
      } catch (err) {
        console.error("Failed to check architectural objects:", err);
        setCanRunML(true); // fallback
      } finally {
        if (!cancelled) setMlChecked(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId]);



  useEffect(() => {
    if (!initialDoc) return;
    if (!mlChecked) return;        // 🔥 wait for check
    if (!canRunML) return;         // 🔥 skip ML

    let isCancelled = false;

    (async () => {
      try {
        setMlLoading(true);
        setMlProgress(20);

        const pdfFileName = initialDoc.split("/").pop();
        const blob = await fetch(initialDoc).then(r => r.blob());

        let formattedScale = scale;
        if (!String(scale).includes(":")) {
          formattedScale = `1:${scale} m`;
        }

        /* ---- SEGMENTATION ---- */
        const segForm = new FormData();
        segForm.append("file", blob, pdfFileName);
        segForm.append("scale", formattedScale);
        segForm.append("dpi", "150");
        segForm.append("conf_threshold", "0.50");
        segForm.append("tile_size", "640");
        segForm.append("overlap", "128");

        // Fetch and pass scale validation data from database
        try {
          const projectResponse = await fetch(`${apiUrl}/api/projects/${projectId}`, {
            credentials: "include",
          });
          
          if (!projectResponse.ok) {
            console.error("Failed to fetch project:", projectResponse.status);
          } else {
            const projectData = await projectResponse.json();
           
            
            // Handle both single object and array responses
            const project = Array.isArray(projectData) ? projectData[0] : projectData;
            
            // HARSHIT CHANGE: Better scale validation data handling
            if (project && project.scale_validation) {
              // Check if we have valid vector data (not null values)
              if (project.scale_validation.vector_length_pt && 
                  project.scale_validation.dimension_mm && 
                  project.scale_validation.vector_length_pt !== null &&
                  project.scale_validation.dimension_mm !== null) {
                
                const dbScaleData = JSON.stringify({
                  page: project.scale_validation.page || 1,
                  vector_length_pt: project.scale_validation.vector_length_pt,
                  dimension_mm: project.scale_validation.dimension_mm
                });
                segForm.append("db_scale_data", dbScaleData);
                console.log("✅ Using valid scale validation data from DB:", dbScaleData);
              } else {
                console.warn("⚠️ Scale validation data in DB has null values, will use formula fallback");
              }
            } else {
              console.warn("No scale validation data in DB, will use formula fallback");
            }
          }
        } catch (err) {
          console.error("Failed to fetch scale validation data:", err);
        }
              console.log('hjk')
        const segOut = await fetch(`${python}/ml-analyze-areas/`, {
          method: "POST",
          // headers: { Authorization: `Bearer ${token}` }, 
          credentials: "include",
          body: segForm,
         
        }).then(r => r.json());

        if (isCancelled) return;
        setMlProgress(30);

        /* ---- DETECTION ---- */
        const detForm = new FormData();
        detForm.append("file", blob, pdfFileName);
        detForm.append("dpi", "150");
        detForm.append("conf_threshold", "0.50");
        detForm.append("tile_size", "640");
        detForm.append("overlap", "128");
        detForm.append("max_detections", "1000");

        const detOut = await fetch(`${python}/ml-detect-objects/`, {
          method: "POST",
          // headers:{Authorization: `Bearer ${token}`},
          credentials: "include",
          body: detForm,
          
        }).then(r => r.json());

        if (isCancelled) return;
        setMlProgress(40);

                /* ---- CALLOUT ---- */
        const calloutForm = new FormData();
        calloutForm.append("file", blob, pdfFileName);
        calloutForm.append("dpi", "120");
        calloutForm.append("conf_threshold", "0.15");
        calloutForm.append("tile_size", "960");
        calloutForm.append("overlap", "200");
        calloutForm.append("max_detections", "150");


        const calloutOut = await fetch(`${python}/ml-detect-callouts/`, {
          method: "POST",
          // headers:{Authorization: `Bearer ${token}`},
          credentials: "include",
          body: calloutForm,
        }).then(r => r.json());

        if (isCancelled) return;
        setMlProgress(55);

        /* ---- WALL ---- */
        const wallForm = new FormData();
        wallForm.append("file", blob, pdfFileName);
        wallForm.append("dpi", "150");
        wallForm.append("conf_threshold", "0.40");
        wallForm.append("tile_size", "640");
        wallForm.append("overlap", "128");
        wallForm.append("scale", formattedScale);

        const wallOut = await fetch(`${python}/ml-detect-walls/`, {
          method: "POST",
          credentials:"include",
          // headers:{Authorization: `Bearer ${token}`},
          body: wallForm,
        }).then(r => r.json());

        if (isCancelled) return;
        setMlProgress(70);

        /* ---- STORE ---- */
        await fetch(
          `${import.meta.env.VITE_API_URL}/api/takeoff/store-ml-response`,
          {
            method: "POST",
            credentials: "include",
            headers: {
               "Content-Type": "application/json",
              //  "Authorization": `Bearer ${token}`,
                          
             },
            body: JSON.stringify({
              id: projectId,
              project_name: projectName,
              segmentation_response: segOut?.pages || [],
              detection_response: detOut?.pages || [],
              wall_response: wallOut?.pages || [],
              callout_response: calloutOut?.pages || []   //  NEW

            }),
          }
        );

        if (!isCancelled) setMlProgress(100);

      } catch (err) {
        console.error("ML failed:", err);
      } finally {
        if (!isCancelled) {
          setTimeout(() => {
            setMlLoading(false);
            setMlProgress(0);
          }, 400);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };

  }, [initialDoc, mlChecked, canRunML, scale, python, projectId, projectName]);








  // Setup WebViewer + click handling
  useEffect(() => {
    if (!viewerRef.current) return;

    WebViewer(
      {
        path: "/lib",
        initialDoc,
        fullAPI: true,
        enableMeasurement: true,
        licenseKey:
          "demo:1761123365955:6037f7980300000000c02284b2ab837864896375330574920f025b561c",
        measurement: {
          scale: scale,
          unit: "m",
        },
      },
      viewerRef.current
    ).then((instance) => {
      instanceRef.current = instance;
      const Core = instance.Core;
      const docViewer = Core.documentViewer;

      // Wait for document to load before setting scale
      docViewer.addEventListener('documentLoaded', () => {
        const scaleArray = parseScaleString(scale);
        if (scaleArray) {
          const tools = [
            "AnnotationCreateDistanceMeasurement",
            "AnnotationCreatePerimeterMeasurement",
            "AnnotationCreateAreaMeasurement",
          ];

          for (const t of tools) {
            const tool = docViewer.getTool(t);
            if (!tool?.setStyles) continue;

            tool.setStyles({
              Scale: scaleArray,
              Precision: 0.001,
            });

            tool.defaults = tool.defaults || {};
            tool.defaults.Scale = scaleArray;
            tool.defaults.Precision = 0.001;
          }

          console.log(" Measurement scale applied:", scaleArray);
        }
      });


      // helper: convert DOM viewer coords -> correct PDF coordinates even when zoomed/panned
      const domToPdfPoint = (pageNum, viewerX, viewerY) => {
        // Use display mode matrix inverse to map viewer (display) coordinates to PDF coordinates
        const dm = docViewer.getDisplayModeManager().getDisplayMode();
        const pageMatrix = dm.getMatrix(pageNum);
        const inv = pageMatrix.inverse();
        const pt = new Core.Math.Point(viewerX, viewerY);
        const pdfPt = inv.transformPoint(pt);
        // pdfPt is a Core.Math.Point with x,y in PDF points
        return { x: pdfPt.x, y: pdfPt.y };
      };

      docViewer.addEventListener("mouseLeftDown", async (e) => {
        const pageNum = docViewer.getCurrentPage();
        const doc = docViewer.getDocument();

        const scrollView = docViewer.getScrollViewElement();
        const rect = scrollView.getBoundingClientRect();
        const viewerX = e.clientX - rect.left;
        const viewerY = e.clientY - rect.top;

        // convert to PDF point correctly (handles zoom/pan)
        let pdfPt;
        try {
          pdfPt = domToPdfPoint(pageNum, viewerX, viewerY);
        } catch (err) {
          // fallback to getPDFCoordinates if something unusual
          const fallback = doc.getPDFCoordinates(pageNum, viewerX, viewerY);
          pdfPt = fallback || { x: viewerX, y: viewerY };
        }

        if (!pdfPt) return;

        const pdfFileName = initialDoc.split("/").pop();
        const DOUBLE_MS = 280;

        // double click detection
        if (clickTimer.current) {
          clearTimeout(clickTimer.current);
          clickTimer.current = null;

          try {
            // 1) Try match-click for both segmentation and detection
            const matchRes = await fetch(`${python}/match-click/`, {
              method: "POST",
              credentials: "include",
              headers: { 
                "Content-Type": "application/json",
                // "Authorization": `Bearer ${token}`,
              },
              body: JSON.stringify({
                x: pdfPt.x,
                y: pdfPt.y,
                pageNum,
                pdfPath: pdfFileName,
              }),
            });
            const match = await matchRes.json();
            console.log("double-click match:", match);

            // Handle segmentation results (rooms with polygons and area)
            if (match && match.status === "success" && match.room) {
              const roomPolygon = match.room.polygon_points;
              if (roomPolygon && roomPolygon.length > 0) {
                // Convert PDF polygon to normalized coordinates for drawing
                const doc = docViewer.getDocument();
                const pageInfo = doc.getPageInfo(pageNum);
                const pageW = pageInfo.width;
                const pageH = pageInfo.height;
                const normalizedPolygon = roomPolygon.map(([x, y]) => [
                  x / pageW,
                  1 - (y / pageH)
                ]);
                await drawPolygon(pageNum, normalizedPolygon);
                console.log("Room measurements:", match.room.measurements);
              }
            }
            
            // Handle segmentation results (polygons with area)
            if (match && match.status === "success" && match.region && match.region.polygon) {
              await drawPolygon(pageNum, match.region.polygon);
            }
            
            // Handle detection results (bounding boxes)
            if (match && match.status === "success" && match.detection) {
              const bbox = match.detection.bbox_coordinates;
              const className = match.detection.class;
              console.log("Detection found:", className, bbox);
              // Support door, window, and zone classes
              if (['door', 'window', 'zone'].includes(className)) {
                await drawBoundingBox(pageNum, bbox, className);
              }
            }



            // Handle callout results
            if (match && match.status === "success" && match.callout) {
              const polygon = match.callout.polygon_points;
              if (polygon && polygon.length > 0) {
                const doc = docViewer.getDocument();
                const pageInfo = doc.getPageInfo(pageNum);
                const pageW = pageInfo.width;
                const pageH = pageInfo.height;

                const normalizedPolygon = polygon.map(([x, y]) => [
                  x / pageW,
                  1 - (y / pageH),
                ]);

                await drawPolygon(pageNum, normalizedPolygon);
              }
            }

            // Handle wall results (polygons with measurements)
            if (match && match.status === "success" && match.wall) {
              const wallPolygon = match.wall.polygon_points;
              if (wallPolygon && wallPolygon.length > 0) {
                // Convert PDF polygon to normalized coordinates for drawing
                const doc = docViewer.getDocument();
                const pageInfo = doc.getPageInfo(pageNum);
                const pageW = pageInfo.width;
                const pageH = pageInfo.height;
                const normalizedPolygon = wallPolygon.map(([x, y]) => [
                  x / pageW,
                  1 - (y / pageH) // Convert to normalized with y from top
                ]);
                await drawPolygon(pageNum, normalizedPolygon);
                console.log("Wall measurements:", match.wall.measurements);
              }
            }

            // 2) analyze_area (only works with segmentation)
            const areaRes = await fetch(`${python}/analyze_area`, {
              method: "POST",
              credentials: "include",
              headers: {
                 "Content-Type": "application/json",
                //  "Authorization": `Bearer ${token}`,
                },
              body: JSON.stringify({
                x: pdfPt.x,
                y: pdfPt.y,
                pageNum,
                pdfPath: pdfFileName,
              }),
            });
            const areaJson = await areaRes.json();
            console.log("double-click area:", areaJson);

            

            // 3) Try detect-object for detection-specific results
            const detectRes = await fetch(`${python}/detect-object/`, {
              method: "POST",
              credentials: "include",
              headers: {
                 "Content-Type": "application/json",
                //  "Authorization": `Bearer ${token}`,
                },
              body: JSON.stringify({
                x: pdfPt.x,
                y: pdfPt.y,
                pageNum,
                pdfPath: pdfFileName,
              }),
            });
            const detectJson = await detectRes.json();
            console.log("double-click detection:", detectJson);

            // -------------------------------------------
            // ✅ STORE TAKEOFF RESULT INTO BACKEND
            // -------------------------------------------

            // extract values
            const region_id = match?.region?.region_id || match?.region?.uuid;
            const area_m2 = areaJson?.area;
            const room_name =
              match?.region?.label || match?.region?.room_name || "Unknown";
            const project_name =
              localStorage.getItem("current_project") || "DefaultProject";

            if (area_m2 && region_id) {
              try {
                await fetch(
                  `${import.meta.env.VITE_API_URL}/api/takeoff/store`,
                  {
                    method: "POST",
                    credentials: "include",
                    headers: { 
                      "Content-Type": "application/json",
                      // "Authorization": `Bearer ${token}`,
                     },
                    body: JSON.stringify({
                      project_name,
                      room_name,
                      region_id,
                      area_m2,
                    }),
                  }
                );
                console.log("Takeoff stored successfully");
              } catch (err) {
                console.error("Failed to store takeoff:", err);
              }
            }
            // -------------------------------------------
          } catch (err) {
            console.error("Double-click error:", err);
          }

          return;
        }

        // schedule single click (in case not double)
        clickTimer.current = setTimeout(async () => {
          clickTimer.current = null;
          try {
            const matchRes = await fetch(`${python}/match-click/`, {
              method: "POST",
              credentials: "include",
              headers: { 
                "Content-Type": "application/json",
                // "Authorization": `Bearer ${token}`,
              },
              body: JSON.stringify({
                x: pdfPt.x,
                y: pdfPt.y,
                pageNum,
                pdfPath: pdfFileName,
              }),
            });
            const match = await matchRes.json();
            console.log("single-click match:", match);
            
            // Handle segmentation results
            if (match && match.status === "success" && match.region && match.region.polygon) {
              await drawPolygon(pageNum, match.region.polygon);
              console.log("centroid (normalized):", match.region.centroid);
            }
            
            // Handle detection results
            if (match && match.status === "success" && match.detection) {
              const bbox = match.detection.bbox_coordinates;
              const className = match.detection.class;
              console.log("Detection found:", className, bbox);
              // Support door, window, and zone classes
              if (['door', 'window', 'zone'].includes(className)) {
                await drawBoundingBox(pageNum, bbox, className);
              }
            }

            // Handle wall results
            if (match && match.status === "success" && match.wall) {
              const wallPolygon = match.wall.polygon_points;
              if (wallPolygon && wallPolygon.length > 0) {
                // Convert PDF polygon to normalized coordinates for drawing
                const doc = docViewer.getDocument();
                const pageInfo = doc.getPageInfo(pageNum);
                const pageW = pageInfo.width;
                const pageH = pageInfo.height;
                const normalizedPolygon = wallPolygon.map(([x, y]) => [
                  x / pageW,
                  1 - (y / pageH) // Convert to normalized with y from top
                ]);
                await drawPolygon(pageNum, normalizedPolygon);
                console.log("Wall measurements:", match.wall.measurements);
              }
            }
          } catch (err) {
            console.error("Single-click error:", err);
          }
        }, DOUBLE_MS);
      });

      if (onReady) onReady(instance);
    });

    // cleanup
    return () => {
      if (clickTimer.current) clearTimeout(clickTimer.current);
    };
  }, [initialDoc, onReady, python, scale]);

  return (
    <div style={{ ...style, position: "relative" }}>

  {mlLoading && (
  <div
    style={{
      position: "absolute",
      inset: 0,
      background: "rgba(0,0,0,0.55)",
      zIndex: 9999,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      color: "#fff",
    }}
  >
    <div className="ml-spinner" />

    <div style={{ marginTop: 6, fontSize: 14 }}>
      {mlProgress}% completed
    </div>

    {/* Progress bar */}
    <div
      style={{
        width: 240,
        height: 6,
        background: "rgba(255,255,255,0.25)",
        borderRadius: 4,
        marginTop: 12,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: `${mlProgress}%`,
          height: "100%",
          background: "#4ade80",
          transition: "width 0.4s ease",
        }}
      />
    </div>
  </div>
)}


      <div ref={viewerRef} style={{ width: "100%", height: "100%" }} />
    </div>
  );
};

export default FileViewer;